import { useCallback, useEffect, useMemo, useState } from 'react';
import { Input } from '../ui/Input/Input';
import { PrimaryButton, WhiteButton } from '../ui/Button/Buttons';
import { useAppSelector } from '../../hooks/redux';
import { inviteUser, InviteUserRequest } from '../../services/TeamService';
import {
  EMAIL_REGEX,
  ORG_SLUG_MAX_LENGTH
} from '../../constants/Validation.constants';
import { useDispatch } from 'react-redux';
import {
  inviteUserFailure,
  inviteUserStart,
  inviteUserSuccess,
  setIsUserInvited
} from '../../store/slices/userManagementSlice';
import DropdownPortal from '../ui/DropdownPortal/DropdownPortal';
import { PlatformRole } from '../../types/Team.types';
import { getPlatformRoleLabel } from '../../utils/Functions.utils';
import { getApiErrorMessage, isApiErrorCode } from '../../utils/apiError';
import { deriveOrgSlug, validateOrgSlug } from '../../utils/Organization.util';
import { getOrganizations } from '../../services/OrganizationService';

const validateOrgName = (orgName: string): string =>
  orgName.trim() ? '' : 'Organization name is required.';

const validateOrganizationId = (organizationId: string): string =>
  organizationId ? '' : 'Please select an organization.';

type InviteUserProps = Readonly<{
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  registeredUserEmail?: string;
  userRoles?: PlatformRole[];
  showOrgFields?: boolean;
}>;

export function InviteUser({
  setIsOpen,
  registeredUserEmail,
  userRoles = [],
  showOrgFields
}: InviteUserProps) {
  const { users } = useAppSelector((state) => state.userManagement);

  const [email, setEmail] = useState(registeredUserEmail || '');
  const [role, setRole] = useState<PlatformRole | ''>(
    userRoles.length === 1 ? userRoles[0] : ''
  );
  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [orgSlugEdited, setOrgSlugEdited] = useState(false);
  // Org mode for an ADMIN invite: 'new' creates an organization (orgName +
  // orgSlug), 'existing' adds the ADMIN to one the superadmin picks
  // (organizationId). Defaults to 'new' to preserve the prior behavior.
  const [orgMode, setOrgMode] = useState<'new' | 'existing'>('new');
  const [organizationId, setOrganizationId] = useState('');
  const [organizationOptions, setOrganizationOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [organizationsLoaded, setOrganizationsLoaded] = useState(false);
  const [organizationsError, setOrganizationsError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [orgNameError, setOrgNameError] = useState('');
  const [orgSlugError, setOrgSlugError] = useState('');
  const [organizationIdError, setOrganizationIdError] = useState('');
  const [formError, setFormError] = useState('');
  const [touchedEmail, setTouchedEmail] = useState(false);
  const [touchedOrgName, setTouchedOrgName] = useState(false);
  const [touchedOrgSlug, setTouchedOrgSlug] = useState(false);
  const [touchedOrganization, setTouchedOrganization] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();

  // Inviting an ADMIN is what creates a new organization; a USER inherits the
  // inviter's organization and must not carry these fields. showOrgFields is the
  // caller's superadmin check — only a superadmin creates organizations — and is
  // deliberately independent of the deployment: an ADMIN invite creates an
  // organization whether or not the deployment is single-tenant, so do not gate
  // this on the private-cloud flag.
  const needsOrgFields = Boolean(showOrgFields) && role === 'ADMIN';
  // Within an ADMIN invite the superadmin picks exactly one org mode; the two are
  // mutually exclusive in the request body.
  const isNewOrg = needsOrgFields && orgMode === 'new';
  const isExistingOrg = needsOrgFields && orgMode === 'existing';

  const roleOptions = useMemo(
    () =>
      userRoles.map((value) => ({ value, label: getPlatformRoleLabel(value) })),
    [userRoles]
  );

  const validateEmail = useCallback(
    (email: string): string => {
      if (!email) return 'Email is required';
      if (!EMAIL_REGEX.test(email)) return 'Invalid email format.';

      // Only the page currently loaded can be checked here, so this is a
      // courtesy check — the server is the authority and answers
      // USER_ALREADY_IN_ORGANIZATION or PLATFORM_INVITATION_DUPLICATE_PENDING,
      // both of which showInviteError puts on this field.
      const isAlreadyListed = users?.items.some(
        (item) => item.email.toLowerCase() === email.toLowerCase()
      );
      if (isAlreadyListed) return 'User already invited';

      return '';
    },
    [users]
  );

  useEffect(() => {
    if (userRoles.length === 1) {
      setRole(userRoles[0]);
      return;
    }

    if (role && !userRoles.includes(role)) {
      setRole('');
    }
  }, [role, userRoles]);

  useEffect(() => {
    if (touchedEmail) {
      setEmailError(validateEmail(email));
    }
  }, [email, touchedEmail, validateEmail]);

  useEffect(() => {
    if (touchedOrgName) {
      setOrgNameError(validateOrgName(orgName));
    }
  }, [orgName, touchedOrgName]);

  useEffect(() => {
    if (touchedOrgSlug) {
      setOrgSlugError(validateOrgSlug(orgSlug));
    }
  }, [orgSlug, touchedOrgSlug]);

  // Leaving the ADMIN role drops any existing-org selection so a stale
  // organizationId can never be submitted, and resets the mode so re-selecting
  // ADMIN starts on the default 'new' path.
  useEffect(() => {
    if (needsOrgFields) return;
    setOrgMode('new');
    setOrganizationId('');
    setOrganizationIdError('');
    setTouchedOrganization(false);
  }, [needsOrgFields]);

  // Load the ACTIVE organizations for the picker the first time the superadmin
  // enters existing-org mode. getOrganizations defaults to status=ACTIVE, so a
  // PENDING_ADMIN org (which the invite would reject) is never offered.
  useEffect(() => {
    if (!isExistingOrg || organizationsLoaded) return;

    let cancelled = false;
    setOrganizationsLoading(true);
    setOrganizationsError('');

    getOrganizations()
      .then((response) => {
        if (cancelled) return;
        setOrganizationOptions(
          response.data.items.map((organization) => ({
            value: organization.id,
            label: organization.name
          }))
        );
      })
      .catch((error) => {
        if (cancelled) return;
        setOrganizationsError(
          getApiErrorMessage(
            error,
            'Could not load organizations. Please try again.'
          )
        );
      })
      .finally(() => {
        if (cancelled) return;
        // Mark the load attempted on both success and failure so a persistent
        // error can't retrigger this effect into an infinite fetch loop.
        setOrganizationsLoaded(true);
        setOrganizationsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isExistingOrg, organizationsLoaded]);

  const isFormValid = useMemo(() => {
    if (validateEmail(email) !== '' || role === '') return false;

    if (!needsOrgFields) return true;

    return orgMode === 'existing'
      ? validateOrganizationId(organizationId) === ''
      : validateOrgName(orgName) === '' && validateOrgSlug(orgSlug) === '';
  }, [
    email,
    needsOrgFields,
    orgMode,
    organizationId,
    orgName,
    orgSlug,
    role,
    validateEmail
  ]);

  const handleOrgModeChange = (mode: 'new' | 'existing') => {
    setOrgMode(mode);
    // Clear the other mode's field errors and any prior form-level error so a
    // stale message can't linger after switching.
    setFormError('');
    if (mode === 'new') {
      setOrganizationIdError('');
      setTouchedOrganization(false);
    } else {
      setOrgNameError('');
      setOrgSlugError('');
    }
  };

  const handleOrgNameChange = (value: string) => {
    setOrgName(value);

    // Suggest an identifier until the superadmin takes over the field.
    if (!orgSlugEdited) {
      setOrgSlug(deriveOrgSlug(value));
    }
  };

  const handleOrgSlugChange = (value: string) => {
    setOrgSlugEdited(true);
    setOrgSlug(value);
  };

  // Shows the failure on the field that caused it. The touched flags are already
  // set by handleInviteUser, so a server error is always visible.
  const showInviteError = (error: unknown) => {
    const message = getApiErrorMessage(
      error,
      'Failed to send the invitation. Please try again.'
    );

    if (
      isApiErrorCode(
        error,
        'ORG_SLUG_TAKEN',
        'ORG_SLUG_INVALID',
        'ORG_SLUG_RESERVED',
        'ORG_SLUG_REQUIRED'
      )
    ) {
      setOrgSlugError(message);
    } else if (isApiErrorCode(error, 'ORG_NAME_REQUIRED')) {
      setOrgNameError(message);
    } else if (
      isApiErrorCode(error, 'ORGANIZATION_NOT_FOUND', 'ORG_NOT_ACTIVE')
    ) {
      // The chosen existing organization is gone or no longer active — show it on
      // the picker so the superadmin can pick another.
      setOrganizationIdError(message);
    } else if (
      isApiErrorCode(
        error,
        'USER_ALREADY_IN_ORGANIZATION',
        'PLATFORM_INVITATION_DUPLICATE_PENDING',
        'PLATFORM_INVITATION_ALREADY_ACCEPTED',
        'PLATFORM_INVITATION_ALREADY_EXPIRED',
        'PLATFORM_INVITATION_ALREADY_REVOKED'
      )
    ) {
      setEmailError(message);
    } else {
      // Includes ORG_FIELDS_NOT_ALLOWED and USER_NOT_AUTHORIZED, which are not
      // tied to one field.
      setFormError(message);
    }
  };

  const handleInviteUser = async () => {
    setTouchedEmail(true);
    setTouchedOrgName(isNewOrg);
    setTouchedOrgSlug(isNewOrg);
    setTouchedOrganization(isExistingOrg);
    setFormError('');

    const emailValidationError = validateEmail(email);
    const orgNameValidationError = isNewOrg ? validateOrgName(orgName) : '';
    const orgSlugValidationError = isNewOrg ? validateOrgSlug(orgSlug) : '';
    const organizationIdValidationError = isExistingOrg
      ? validateOrganizationId(organizationId)
      : '';

    setEmailError(emailValidationError);
    setOrgNameError(orgNameValidationError);
    setOrgSlugError(orgSlugValidationError);
    setOrganizationIdError(organizationIdValidationError);

    if (
      !role ||
      emailValidationError ||
      orgNameValidationError ||
      orgSlugValidationError ||
      organizationIdValidationError
    ) {
      return;
    }

    const inviteData: InviteUserRequest = {
      email,
      role,
      ...(isNewOrg
        ? { orgName: orgName.trim(), orgSlug: orgSlug.trim() }
        : {}),
      ...(isExistingOrg ? { organizationId } : {})
    };

    setIsSubmitting(true);
    dispatch(inviteUserStart());
    try {
      await inviteUser(inviteData);
      dispatch(inviteUserSuccess());
      dispatch(setIsUserInvited(true));
      setIsOpen(false);
    } catch (error) {
      dispatch(inviteUserFailure());
      showInviteError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col flex-start gap-6">
      <h2 className="text-lg leading-6 font-medium text-gray-900">
        Invite User
      </h2>

      <div className="flex flex-col w-full max-w-[740px] gap-6">
        <Input
          label="Email Address"
          placeholder="Enter email"
          id="email"
          type="email"
          required={true}
          value={email}
          maxLength={254}
          onBlur={() => {
            setTouchedEmail(true);
            setEmailError(validateEmail(email));
          }}
          onChange={(e) => setEmail(e.target.value)}
          error={touchedEmail ? emailError : ''}
          labelClassName="label"
          inputClassName="input"
        />

        <DropdownPortal
          label="Role"
          placeholder="Choose role"
          options={roleOptions}
          allOptions={roleOptions}
          value={role}
          disabled={userRoles.length <= 1}
          onChange={(value) => setRole(value as PlatformRole)}
        />

        {needsOrgFields && (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1.5">
              <span className="text-gray-800 text-left font-inter font-medium text-sm leading-5">
                Organization
              </span>
              <div className="flex w-fit gap-1 rounded-md border border-gray-350 p-1">
                <button
                  type="button"
                  onClick={() => handleOrgModeChange('new')}
                  className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                    orgMode === 'new'
                      ? 'bg-purple-700 text-white'
                      : 'text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  Create new organization
                </button>
                <button
                  type="button"
                  onClick={() => handleOrgModeChange('existing')}
                  className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                    orgMode === 'existing'
                      ? 'bg-purple-700 text-white'
                      : 'text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  Add to existing organization
                </button>
              </div>
            </div>

            {isNewOrg ? (
              <>
                <Input
                  label="Organization Name"
                  placeholder="Enter organization name"
                  id="orgName"
                  type="text"
                  required={true}
                  value={orgName}
                  maxLength={254}
                  onBlur={() => {
                    setTouchedOrgName(true);
                    setOrgNameError(validateOrgName(orgName));
                  }}
                  onChange={(e) => handleOrgNameChange(e.target.value)}
                  error={touchedOrgName ? orgNameError : ''}
                  labelClassName="label"
                  inputClassName="input"
                />
                <div className="flex flex-col gap-1">
                  <Input
                    label="Organization ID"
                    placeholder="acme"
                    id="orgSlug"
                    type="text"
                    required={true}
                    value={orgSlug}
                    maxLength={ORG_SLUG_MAX_LENGTH}
                    onBlur={() => {
                      setTouchedOrgSlug(true);
                      setOrgSlugError(validateOrgSlug(orgSlug));
                    }}
                    onChange={(e) => handleOrgSlugChange(e.target.value)}
                    error={touchedOrgSlug ? orgSlugError : ''}
                    labelClassName="label"
                    inputClassName="input"
                  />
                  <p className="text-sm text-gray-500 text-left">
                    This cannot be changed after the invitation is sent.
                  </p>
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-1">
                <DropdownPortal
                  label="Select organization"
                  placeholder={
                    organizationsLoading
                      ? 'Loading organizations...'
                      : 'Select an organization'
                  }
                  options={organizationOptions}
                  allOptions={organizationOptions}
                  value={organizationId}
                  searchable
                  searchInTrigger
                  searchPlaceholder="Search organizations"
                  noOptionsMessage={
                    organizationsLoading
                      ? 'Loading...'
                      : 'No active organizations found'
                  }
                  disabled={organizationsLoading}
                  error={touchedOrganization ? organizationIdError : ''}
                  onChange={(value) => {
                    setOrganizationId(value);
                    setTouchedOrganization(true);
                    setOrganizationIdError('');
                  }}
                />
                {organizationsError && (
                  <p className="text-sm text-rose-800 text-left">
                    {organizationsError}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {formError && (
        <p className="text-sm text-rose-800 text-left">{formError}</p>
      )}

      <div className="flex flex-row justify-end items-center gap-3">
        <WhiteButton onClick={() => setIsOpen(false)}>Cancel</WhiteButton>
        <PrimaryButton
          disabled={!isFormValid || isSubmitting}
          onClick={handleInviteUser}
        >
          {isSubmitting ? 'Inviting...' : 'Invite User'}
        </PrimaryButton>
      </div>
    </div>
  );
}
